import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Plus, X, ShoppingBag, Star } from "lucide-react";
import { getRestaurantMenu } from "../lib/api";
import { useCartStore } from "../store/cartStore";

const GREEN = "#075A43";
const ORANGE = "#FF5A1F";
const GOLD = "#C9A227";

export default function PartnerRestaurant() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [activeCat, setActiveCat] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const addItem = useCartStore((s) => s.addItem);
  const cartCount = useCartStore((s) => s.getItemCount());

  const load = () => {
    setLoading(true);
    setError(null);
    getRestaurantMenu(id)
      .then((d) => {
        setData(d);
        const first = d.categories.find((c) => (c.items || []).length > 0) || d.categories[0];
        setActiveCat(first ? first.id : null);
      })
      .catch(() => setError("Impossible de charger la carte de ce restaurant."))
      .finally(() => setLoading(false));
  };
  useEffect(load, [id]);

  const handleAdd = (item) => {
    addItem({
      id: `rest_${item.id}`, title: item.name, price: item.price, image_url: item.image_url || undefined,
      promoKind: item.promo_active ? item.promo_kind : null,
      originalPrice: item.original_price,
      promoLabel: item.promo_active ? item.promo_label : null,
    });
    toast.success(`${item.name} ajouté au panier`);
  };

  const currentCat = data?.categories.find((c) => c.id === activeCat);
  const items = currentCat?.items || [];

  return (
    <div className="max-w-6xl mx-auto px-4 py-8" data-testid="pr-page">
      <button onClick={() => nav("/restaurant-boissons")} data-testid="pr-back"
        className="flex items-center gap-1 text-sm font-bold mb-4" style={{ color: GREEN }}>
        <ArrowLeft size={18} /> Restaurant &amp; Boissons
      </button>

      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-testid="pr-loading">
          {[...Array(6)].map((_, i) => <div key={i} className="h-56 rounded-2xl animate-pulse" style={{ background: "#e9edf2" }} />)}
        </div>
      ) : error ? (
        <div className="text-center py-10">
          <p style={{ color: "#64748b" }}>{error}</p>
          <button data-testid="pr-retry" onClick={load} className="mt-4 rounded-xl font-bold text-white px-5 py-2" style={{ background: GREEN }}>Réessayer</button>
        </div>
      ) : (
        <>
          <h1 className="text-3xl font-extrabold mb-4" style={{ color: GREEN }}>{data?.restaurant?.name}</h1>

          {/* Onglets catégories */}
          <div className="flex gap-2 overflow-x-auto pb-2 mb-6">
            {(data?.categories || []).map((c) => (
              <button key={c.id} data-testid={`pr-cat-${c.id}`} onClick={() => setActiveCat(c.id)}
                className="px-4 py-2 rounded-full text-sm font-semibold flex-shrink-0 transition-colors"
                style={activeCat === c.id
                  ? { background: GREEN, color: "#fff" }
                  : { background: "#f5f7f6", color: "#1f2937", border: "1px solid #e5e7eb" }}>
                {c.name}
              </button>
            ))}
          </div>

          <h2 className="text-xl font-extrabold mb-4" style={{ color: GREEN }}>{currentCat?.name || ""}</h2>

          {items.length === 0 ? (
            <p className="text-center py-10" style={{ color: "#64748b" }} data-testid="pr-empty-cat">Aucun produit dans cette catégorie.</p>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 pb-24">
              {items.map((item) => {
                const unavailable = item.is_available === false;
                return (
                  <div key={item.id} data-testid={`pr-item-${item.id}`}
                    className="rounded-2xl overflow-hidden bg-white flex flex-col" style={{ border: "1px solid #f0f0f0" }}>
                    <div className="relative h-36 flex items-center justify-center" style={{ background: "#f5f7f6" }}>
                      {item.image_url
                        ? <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" />
                        : <ShoppingBag size={34} color="#cbd5e1" />}
                      {item.promo_active && (
                        <span data-testid={`pr-promo-${item.id}`} className="absolute left-3 top-3 text-[11px] font-extrabold px-2 py-1 rounded-full text-white" style={{ background: ORANGE }}>
                          {item.promo_label || `-${Number(item.promo?.percent || 0).toFixed(0)}%`}
                        </span>
                      )}
                      {item.recommended && (
                        <span data-testid={`pr-reco-${item.id}`} className="absolute right-3 top-3 inline-flex items-center gap-1 text-[11px] font-extrabold px-2 py-1 rounded-full" style={{ background: "#fffdf5", color: GOLD, border: `1px solid ${GOLD}` }}>
                          <Star size={11} /> Recommandé
                        </span>
                      )}
                      <button data-testid={`pr-add-${item.id}`} disabled={unavailable} onClick={() => handleAdd(item)}
                        className="absolute right-3 bottom-3 w-11 h-11 rounded-full flex items-center justify-center text-white"
                        style={{ background: unavailable ? "#9ca3af" : GREEN, boxShadow: "0 2px 6px rgba(0,0,0,0.2)" }}>
                        {unavailable ? <X size={22} /> : <Plus size={22} />}
                      </button>
                    </div>
                    <div className="p-3">
                      <p className="font-bold truncate" style={{ color: "#1f2937" }}>{item.name}</p>
                      {item.promo_active && Number(item.effective_price ?? item.price) < Number(item.original_price ?? item.price) ? (
                        <p className="font-extrabold flex items-center gap-2">
                          <span style={{ textDecoration: "line-through", color: "#94a3b8" }}>CHF {Number(item.original_price ?? item.price).toFixed(2)}</span>
                          <span style={{ color: ORANGE }}>CHF {Number(item.price).toFixed(2)}</span>
                        </p>
                      ) : (
                        <p className="font-extrabold" style={{ color: ORANGE }}>CHF {Number(item.price).toFixed(2)}</p>
                      )}
                      {item.description && <p className="text-xs mt-1 line-clamp-2" style={{ color: "#94a3b8" }}>{item.description}</p>}
                      {Array.isArray(item.offerts) && item.offerts.filter((o) => o && o.label).length > 0 && (
                        <p data-testid={`pr-offerts-${item.id}`} className="text-[11px] mt-1" style={{ color: GREEN }}>
                          <span className="font-extrabold">OFFERTS : </span>
                          {item.offerts.filter((o) => o && o.label).map((o) => o.label).join(" + ")}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {cartCount > 0 && (
        <button data-testid="pr-view-cart" onClick={() => nav("/panier")}
          className="fixed bottom-6 left-1/2 -translate-x-1/2 rounded-2xl text-white font-bold px-6 py-3 flex items-center gap-2 z-50"
          style={{ background: GREEN, boxShadow: "0 6px 18px rgba(0,0,0,0.25)" }}>
          <ShoppingBag size={20} /> Voir le panier ({cartCount})
        </button>
      )}
    </div>
  );
}
